/**
 * Limits quantity to 1 for exact SKUs from config on product page and in cart.
 * Config: window.themeVariables.settings.skuQuantityLimit (snippets/js-variables.liquid)
 */
(function () {
  const config = window.themeVariables?.settings?.skuQuantityLimit;

  if (!config?.skus?.length) {
    return;
  }

  const MAX_QUANTITY = config.maxQuantity ?? 1;
  const restrictedSkus = new Set(config.skus);
  const errorMessage =
    window.themeVariables?.strings?.skuQuantityLimitError ||
    "Only one of this item can be added to the cart.";

  function isRestrictedSku(sku) {
    return Boolean(sku && restrictedSkus.has(sku));
  }

  async function fetchCart() {
    const response = await originalFetch(`${Shopify.routes.root}cart.js`);

    if (!response.ok) {
      throw new Error("Unable to load cart.");
    }

    return response.json();
  }

  function createErrorResponse(message) {
    return new Response(JSON.stringify({ description: message, message }), {
      status: 422,
      headers: { "Content-Type": "application/json" },
    });
  }

  /**
   * @param {number} variantId
   * @param {{ items: Array<{ variant_id: number; sku?: string }> }} cart
   */
  function getSkuForVariant(variantId, cart) {
    const numericId = Number(variantId);
    const cartSku = cart.items.find(
      (item) => item.variant_id === numericId,
    )?.sku;

    if (cartSku) {
      return cartSku;
    }

    const variantPicker = document.querySelector("variant-picker");

    if (variantPicker?.selectedVariant?.id === numericId) {
      return variantPicker.selectedVariant.sku;
    }

    const variantScript = document.querySelector("script[data-variant]");

    if (!variantScript) {
      return undefined;
    }

    try {
      const variant = JSON.parse(variantScript.textContent || "null");

      if (variant?.id === numericId) {
        return variant.sku;
      }
    } catch {
      // Ignore malformed variant JSON.
    }

    return undefined;
  }

  /**
   * @param {FormData | Record<string, unknown>} payload
   * @returns {Array<{ id: number; quantity: number }>}
   */
  function parseAddPayload(payload) {
    if (payload instanceof FormData) {
      const itemsEntry = payload.get("items");

      if (itemsEntry) {
        try {
          const parsedItems = JSON.parse(String(itemsEntry));
          return parsedItems.map((item) => ({
            id: Number(item.id),
            quantity: Number(item.quantity || 1),
          }));
        } catch {
          return [];
        }
      }

      const variantId = payload.get("id");

      if (!variantId) {
        return [];
      }

      return [
        {
          id: Number(variantId),
          quantity: Number(payload.get("quantity") || 1),
        },
      ];
    }

    if (Array.isArray(payload.items)) {
      return payload.items.map((item) => ({
        id: Number(item.id),
        quantity: Number(item.quantity || 1),
      }));
    }

    if (payload.id) {
      return [
        {
          id: Number(payload.id),
          quantity: Number(payload.quantity || 1),
        },
      ];
    }

    return [];
  }

  /**
   * @param {Array<{ id: number; quantity: number }>} items
   * @param {{ items: Array<{ variant_id: number; quantity: number; sku?: string; key: string }> }} cart
   */
  function validateAddItems(items, cart) {
    for (const item of items) {
      const sku = getSkuForVariant(item.id, cart);

      if (!isRestrictedSku(sku)) {
        continue;
      }

      const existingLine = cart.items.find(
        (lineItem) => lineItem.variant_id === item.id,
      );
      const totalQuantity = (existingLine?.quantity || 0) + item.quantity;

      if (item.quantity > MAX_QUANTITY || totalQuantity > MAX_QUANTITY) {
        return errorMessage;
      }
    }

    return null;
  }

  /**
   * @param {string} lineKey
   * @param {number} targetQuantity
   * @param {{ items: Array<{ sku?: string; key: string }> }} cart
   */
  function validateChangeQuantity(lineKey, targetQuantity, cart) {
    const lineItem = cart.items.find((item) => item.key === lineKey);

    if (!lineItem || !isRestrictedSku(lineItem.sku)) {
      return null;
    }

    if (targetQuantity > MAX_QUANTITY) {
      return errorMessage;
    }

    return null;
  }

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function patchedFetch(input, init = {}) {
    const url =
      typeof input === "string"
        ? input
        : input instanceof Request
          ? input.url
          : "";

    const isCartAdd = url.includes("/cart/add.js");
    const isCartChange = url.includes("/cart/change.js");

    if (!isCartAdd && !isCartChange) {
      return originalFetch(input, init);
    }

    let body = init.body;

    if (input instanceof Request) {
      body = body ?? input.body;
    }

    try {
      const cart = await fetchCart();

      if (isCartAdd) {
        let payload = body;

        if (body instanceof FormData) {
          payload = body;
        } else if (typeof body === "string") {
          try {
            payload = JSON.parse(body);
          } catch {
            payload = {};
          }
        } else {
          payload = {};
        }

        const validationError = validateAddItems(parseAddPayload(payload), cart);

        if (validationError) {
          return createErrorResponse(validationError);
        }
      }

      if (isCartChange) {
        let payload = {};

        if (typeof body === "string") {
          try {
            payload = JSON.parse(body);
          } catch {
            payload = {};
          }
        }

        const lineKey = payload.id;
        const targetQuantity = Number(payload.quantity);

        if (lineKey && !Number.isNaN(targetQuantity)) {
          const validationError = validateChangeQuantity(
            lineKey,
            targetQuantity,
            cart,
          );

          if (validationError) {
            return createErrorResponse(validationError);
          }
        }
      }
    } catch {
      return originalFetch(input, init);
    }

    return originalFetch(input, init);
  };
})();
